import { DynamicValue } from "./DynamicValue";
import { formatTitleFirstWord } from "utils/formatTitle";
import { Row } from "../Row";
import { ObjectItem } from "./ObjectItem";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { HelpCircle } from "untitledui-js-base";
import { FC, ReactNode } from "react";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";

interface DynamicKeyValueProps {
  detailsKey: string;
  value: any;
  isUnavailableValue?: boolean;
  skipEmptyObjects?: boolean;
  fullDate?: boolean;
  order?: string[];
  filter?: string[];
  rename?: Record<string, string | ((object: object) => string)>;
  parentObject?: null | object;
  parentIdx?: number;
  valueTransform?: Record<string, (value: any, row: object) => any>;
  status?: ReactNode;
  actionElement?: ReactNode;
  fullWidthRow?: boolean;
}

const mapAbbrToFull = {
  dob: "Date of birth"
};

const getDetailsKey = (
  detailsKey: string,
  rename: Record<string, string | ((object: object) => string)> | undefined,
  parentObject?: object
) => {
  if (!rename) return detailsKey;

  const key = rename[detailsKey];

  if (!key) return detailsKey;

  if (typeof key === "function") return key(parentObject || {});
  return key;
};

const getTransformedValue = (
  value: any,
  detailsKey: string,
  valueTransform: Record<string, (value: any, row: object) => any>,
  parentObject: object
) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!valueTransform) return value;

  const transform = valueTransform[detailsKey];

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!transform) return value;

  return transform(value, parentObject);
};

const UnavailableValue = () => {
  const { unit } = useUnitsPropertiesDictionary();
  return (
    <Tooltip placement="bottom">
      <TooltipTrigger as="span" className="flex items-center gap-2">
        <HelpCircle className="size-4 stroke-danger-500" />
        <span className="text-xs font-bold">-</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[370px] rounded-lg border bg-alpha-800 p-4 text-sm text-white">
        No data available. Application was moved from a {unit} where this data
        was not requested
      </TooltipContent>
    </Tooltip>
  );
};

export const DynamicKeyValue: FC<DynamicKeyValueProps> = ({
  detailsKey: _dk,
  value: _value,
  skipEmptyObjects,
  fullDate,
  order,
  filter,
  rename,
  parentObject,
  valueTransform,
  parentIdx,
  isUnavailableValue,
  status,
  actionElement,
  fullWidthRow
}) => {
  const detailsKey = getDetailsKey(_dk, rename, parentObject || _value);

  const value = getTransformedValue(
    _value,
    _dk,
    // @ts-expect-error
    valueTransform,
    parentObject || {}
  );

  if (isUnavailableValue)
    return (
      <Row
        fullWidthRow={fullWidthRow}
        className="my-2"
        title={formatTitleFirstWord(mapAbbrToFull[detailsKey] || detailsKey)}
        value={
          <DynamicValue
            detailsKey={detailsKey}
            value={<UnavailableValue />}
            fullDate={fullDate}
          />
        }
      />
    );

  if (
    typeof value === "object" &&
    value !== null &&
    !(
      (value?.asset && value.asset?.url && value.asset?.content_type) ||
      (detailsKey === "asset" && value.url && value.content_type)
    )
  ) {
    if (Array.isArray(value))
      return value.map((v, idx) => (
        <ObjectItem
          detailsKey={`${detailsKey} ${idx + 1}`}
          key={detailsKey}
          order={order}
          filter={filter}
          fullWidthRow={fullWidthRow}
          skipEmptyObjects={skipEmptyObjects}
          value={v}
          parentObject={parentObject || value}
          rename={rename}
          valueTransform={valueTransform}
          fullDate={fullDate}
          status={status}
          actionElement={actionElement}
        />
      ));

    if (
      (skipEmptyObjects && !Object.keys(value || {}).length) ||
      (filter && filter.includes(detailsKey.toLowerCase()))
    )
      return null;

    return (
      <ObjectItem
        fullWidthRow={fullWidthRow}
        skipEmptyObjects={skipEmptyObjects}
        detailsKey={detailsKey}
        order={order}
        filter={filter}
        value={value}
        rename={rename}
        parentObject={parentObject || value}
        valueTransform={valueTransform}
        fullDate={fullDate}
        status={status}
        actionElement={actionElement}
      />
    );
  }

  if (filter && filter.includes(detailsKey.toLowerCase())) return null;

  if (detailsKey === "asset" && value?.url && value?.content_type) {
    const docNum = (parentIdx || 0) + 1;

    let assetName;
    if ((parentObject as { verification_type?: string }).verification_type)
      assetName = `${(parentObject as { verification_type?: string }).verification_type} #${docNum}`;
    else assetName = value.asset?.filename || value?.filename;

    return (
      <DynamicValue
        detailsKey={detailsKey}
        value={value}
        fullDate={fullDate}
        assetName={assetName}
      />
    );
  }

  if (
    typeof value === "object" &&
    value?.asset &&
    value.asset?.url &&
    value.asset?.content_type
  )
    return (
      <div className="mt-4">
        <h3 className="border-b border-alpha-80 pb-2 text-sm font-bold">
          {formatTitleFirstWord(mapAbbrToFull[detailsKey] || detailsKey)}
        </h3>
        <div className="py-4">
          <DynamicValue
            detailsKey={detailsKey}
            value={value}
            fullDate={fullDate}
          />
        </div>
      </div>
    );

  return (
    <Row
      className="my-2"
      fullWidthRow={fullWidthRow}
      title={formatTitleFirstWord(mapAbbrToFull[detailsKey] || detailsKey)}
      value={
        <DynamicValue
          detailsKey={detailsKey}
          value={value}
          fullDate={fullDate}
        />
      }
    />
  );
};
